import React from 'react';
import type { Hint, ItemType } from '../../types';
import { formatNumber as fmt } from '../../lib/format';

interface HintFigureProps {
  hint: Hint;
}

// Diagrams are small, static and need to stay crisp on hi-DPI / when stretched,
// so they are declarative SVG (real <text>, no canvas bitmap scaling). Kinds
// without a useful figure (fractions, plain text) show the step list only.
export const HintFigure: React.FC<HintFigureProps> = ({ hint }) => {
  const figure = renderFigure(hint);
  return (
    <div>
      {figure && (
        <svg
          viewBox="0 0 600 200"
          className="w-full h-auto max-h-[200px]"
          role="img"
          aria-label="ヒントの図"
          fontFamily="sans-serif"
        >
          {figure}
        </svg>
      )}
      <ol className="mt-2 space-y-1">
        {hint.steps.map((s, i) => (
          <li key={i} className="flex gap-2 text-gray-700 text-sm leading-relaxed">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-orange-100 text-orange-600 font-bold text-xs flex items-center justify-center mt-0.5">
              {i + 1}
            </span>
            <span dangerouslySetInnerHTML={{ __html: s }} />
          </li>
        ))}
      </ol>
    </div>
  );
};

function renderFigure(hint: Hint): React.ReactNode {
  switch (hint.kind) {
    case 'numberline':
      return <NumberLine hint={hint} />;
    case 'tape':
      return <Tape hint={hint} />;
    case 'placevalue':
      return <PlaceValue hint={hint} />;
    case 'wasa':
      return <Wasa hint={hint} />;
    case 'bunpai':
      return <Bunpai hint={hint} />;
    case 'rounding':
      return <Rounding hint={hint} />;
    case 'unit':
      return <UnitLadder hint={hint} />;
    case 'deccalc':
      return <DecCalc hint={hint} />;
    case 'deccompose':
      return <DecCompose hint={hint} />;
    default:
      return null; // fractions / text → steps only
  }
}

// ---- shared item glyphs ---------------------------------------------------

const Tree = ({ x, y }: { x: number; y: number }) => (
  <g transform={`translate(${x},${y})`}>
    <rect x={-3} y={-22} width={6} height={22} fill="#795548" />
    <circle cx={0} cy={-26} r={13} fill="#43A047" />
  </g>
);

const Flower = ({ x, y }: { x: number; y: number }) => (
  <g transform={`translate(${x},${y})`}>
    <rect x={-1.5} y={-16} width={3} height={16} fill="#43A047" />
    {[0, 1, 2, 3, 4].map((a) => {
      const ang = (a / 5) * Math.PI * 2;
      return <circle key={a} cx={Math.cos(ang) * 6} cy={-22 + Math.sin(ang) * 6} r={4} fill="#E91E63" />;
    })}
    <circle cx={0} cy={-22} r={4} fill="#FFC107" />
  </g>
);

const Post = ({ x, y }: { x: number; y: number }) => (
  <g>
    <rect x={x - 3} y={y - 40} width={6} height={40} fill="#90A4AE" />
    <rect x={x - 6} y={y - 44} width={12} height={6} fill="#607D8B" />
  </g>
);

const Item = ({ x, y, type }: { x: number; y: number; type: ItemType }) =>
  type === 'flower' ? <Flower x={x} y={y} /> : <Tree x={x} y={y} />;

// ---- numberline (植木算) --------------------------------------------------

const NumberLine = ({ hint }: { hint: Extract<Hint, { kind: 'numberline' }> }) => {
  const { pattern, intervals, gap, total, itemType } = hint;

  if (pattern === 'CIRCULAR') {
    const cx = 300;
    const cy = 100;
    const r = 64;
    return (
      <g>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#81D4FA" strokeWidth={8} />
        {Array.from({ length: intervals }, (_, i) => {
          const ang = -Math.PI / 2 + (i / intervals) * Math.PI * 2;
          return <Item key={i} x={cx + Math.cos(ang) * r} y={cy + Math.sin(ang) * r + 6} type={itemType} />;
        })}
        <text x={cx} y={cy + 2} textAnchor="middle" fontSize={13} fill="#555">
          １周 {total}m ・ {gap}m おき
        </text>
        <text x={cx} y={192} textAnchor="middle" fontSize={13} fill="#555">
          点（木）の数 = 間の数 = {intervals}
        </text>
      </g>
    );
  }

  const x0 = 50;
  const x1 = 550;
  const ly = 150;
  const span = x1 - x0;
  return (
    <g>
      <line x1={x0} y1={ly} x2={x1} y2={ly} stroke="#8D6E63" strokeWidth={6} strokeLinecap="round" />
      {Array.from({ length: intervals }, (_, i) => {
        const mid = x0 + ((i + 0.5) / intervals) * span;
        return (
          <text key={`g${i}`} x={mid} y={ly + 18} textAnchor="middle" fontSize={11} fill="#888">
            {gap}m
          </text>
        );
      })}
      {Array.from({ length: intervals + 1 }, (_, i) => {
        const x = x0 + (i / intervals) * span;
        const isEnd = i === 0 || i === intervals;
        return pattern === 'NEITHER' && isEnd ? (
          <Post key={`i${i}`} x={x} y={ly} />
        ) : (
          <Item key={`i${i}`} x={x} y={ly - 3} type={itemType} />
        );
      })}
      <text x={300} y={194} textAnchor="middle" fontSize={13} fontWeight="bold" fill="#555">
        全長 {total}m（間の数 {intervals}）
      </text>
    </g>
  );
};

// ---- tape (テープ) --------------------------------------------------------

const Tape = ({ hint }: { hint: Extract<Hint, { kind: 'tape' }> }) => {
  const { tapeLen, glueLen, count, totalLen } = hint;
  const x0 = 40;
  const pxPerCm = (600 - 80) / totalLen;
  const tapeW = tapeLen * pxPerCm;
  const glueW = glueLen * pxPerCm;
  const tapeH = 46;
  const cy = 94;
  const top = cy - tapeH / 2;

  const tapes = Array.from({ length: count }, (_, i) => {
    const x = x0 + (tapeW - glueW) * i;
    return (
      <g key={i}>
        <rect x={x} y={top} width={tapeW} height={tapeH} fill={i % 2 === 0 ? '#E3F2FD' : '#BBDEFB'} stroke="#1565C0" strokeWidth={1.5} />
        {i > 0 && <rect x={x} y={top} width={glueW} height={tapeH} fill="rgba(255,152,0,0.35)" />}
      </g>
    );
  });
  const endX = x0 + tapeW + (tapeLen - glueLen) * pxPerCm * (count - 1);

  return (
    <g>
      {tapes}
      <text x={x0 + tapeW / 2} y={top + tapeH + 16} textAnchor="middle" fontSize={12} fill="#333">
        {tapeLen}cm
      </text>
      <text x={x0 + tapeW - glueW / 2} y={top - 14} textAnchor="middle" fontSize={11} fontWeight="bold" fill="#E65100">
        のりしろ
      </text>
      <text x={x0 + tapeW - glueW / 2} y={top - 3} textAnchor="middle" fontSize={11} fontWeight="bold" fill="#E65100">
        {glueLen}cm
      </text>
      <polyline
        points={`${x0},${top + tapeH + 19} ${x0},${top + tapeH + 24} ${endX},${top + tapeH + 24} ${endX},${top + tapeH + 19}`}
        fill="none"
        stroke="#4CAF50"
        strokeWidth={1.5}
      />
      <text x={300} y={top + tapeH + 40} textAnchor="middle" fontSize={13} fontWeight="bold" fill="#2E7D32">
        ぜんぶで {totalLen}cm ・ テープ {count}枚
      </text>
    </g>
  );
};

// ---- place value (桁) -----------------------------------------------------

const PlaceValue = ({ hint }: { hint: Extract<Hint, { kind: 'placevalue' }> }) => {
  const { base, op, factor, result, shift } = hint;
  const cy = 100;
  const ax0 = 228;
  const ax1 = 372;
  const dir = shift > 0 ? '上がる' : '下がる';
  return (
    <g>
      <text x={132} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={40} fontWeight="bold" fill="#374151">
        {fmt(base)}
      </text>
      <line x1={ax0} y1={cy} x2={ax1} y2={cy} stroke="#F57C00" strokeWidth={3} />
      <polygon points={`${ax1},${cy} ${ax1 - 10},${cy - 6} ${ax1 - 10},${cy + 6}`} fill="#F57C00" />
      <text x={(ax0 + ax1) / 2} y={cy - 18} textAnchor="middle" fontSize={15} fontWeight="bold" fill="#F57C00">
        {op}{fmt(factor)}
      </text>
      <text x={(ax0 + ax1) / 2} y={cy + 22} textAnchor="middle" fontSize={12} fill={shift > 0 ? '#2E7D32' : '#1565C0'}>
        位が {Math.abs(shift)}つ {dir}
      </text>
      <text x={480} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={40} fontWeight="bold" fill="#111827">
        {fmt(result)}
      </text>
      {shift > 0 && Number.isInteger(base) && Number.isInteger(result) && (
        <text x={480} y={cy + 34} textAnchor="middle" fontSize={12} fill="#2E7D32">
          0 を {shift}つ つける
        </text>
      )}
    </g>
  );
};

// ---- two-bar diagrams (和差算 / 分配算) -----------------------------------

const BAR_X0 = 56;
const BAR_MAXW = 600 - 170;
const BAR_Y1 = 64;
const BAR_Y2 = 128;
const TICK = 7;

function Bar({ y, w }: { y: number; w: number }) {
  return (
    <g stroke="#333" strokeLinecap="round">
      <line x1={BAR_X0} y1={y} x2={BAR_X0 + w} y2={y} strokeWidth={3} />
      <line x1={BAR_X0} y1={y - TICK} x2={BAR_X0} y2={y + TICK} strokeWidth={2} />
      <line x1={BAR_X0 + w} y1={y - TICK} x2={BAR_X0 + w} y2={y + TICK} strokeWidth={2} />
    </g>
  );
}

function SumBracket({ w, label }: { w: number; label: string }) {
  const bx = BAR_X0 + w + 14;
  return (
    <g>
      <polyline points={`${bx},${BAR_Y1} ${bx + 10},${BAR_Y1} ${bx + 10},${BAR_Y2} ${bx},${BAR_Y2}`} fill="none" stroke="#4CAF50" strokeWidth={2} />
      <text x={bx + 14} y={(BAR_Y1 + BAR_Y2) / 2 + 4} fontSize={13} fontWeight="bold" fill="#2E7D32">
        {label}
      </text>
    </g>
  );
}

function Unknown({ y, w }: { y: number; w: number }) {
  const midX = BAR_X0 + w / 2;
  return (
    <g>
      <path d={`M ${BAR_X0} ${y - 12} Q ${midX} ${y - 28} ${BAR_X0 + w} ${y - 12}`} fill="none" stroke="#F57C00" strokeWidth={2} />
      <text x={midX} y={y - 30} textAnchor="middle" fontSize={16} fontWeight="bold" fill="#F57C00">
        ?
      </text>
    </g>
  );
}

const Wasa = ({ hint }: { hint: Extract<Hint, { kind: 'wasa' }> }) => {
  const { sum, diff, target, bigLabel, smallLabel } = hint;
  const big = (sum + diff) / 2;
  const small = (sum - diff) / 2;
  const topW = BAR_MAXW;
  const botW = (small / big) * BAR_MAXW;
  const diffMid = (BAR_X0 + botW + BAR_X0 + topW) / 2;
  return (
    <g>
      <Bar y={BAR_Y1} w={topW} />
      <Bar y={BAR_Y2} w={botW} />
      <text x={BAR_X0 - 10} y={BAR_Y1 + 4} textAnchor="end" fontSize={13} fontWeight="bold" fill="#333">{bigLabel}</text>
      <text x={BAR_X0 - 10} y={BAR_Y2 + 4} textAnchor="end" fontSize={13} fontWeight="bold" fill="#333">{smallLabel}</text>
      <SumBracket w={topW} label={`合計 ${sum}`} />
      <path d={`M ${BAR_X0 + botW} ${BAR_Y1 + 8} Q ${diffMid} ${BAR_Y1 + 26} ${BAR_X0 + topW} ${BAR_Y1 + 8}`} fill="none" stroke="#E91E63" strokeWidth={2} />
      <text x={diffMid} y={BAR_Y1 + 40} textAnchor="middle" fontSize={12} fontWeight="bold" fill="#E91E63">差 {diff}</text>
      <Unknown y={target === 'BIG' ? BAR_Y1 : BAR_Y2} w={target === 'BIG' ? topW : botW} />
    </g>
  );
};

// ---- rounding (四捨五入・がい数) ------------------------------------------

const Rounding = ({ hint }: { hint: Extract<Hint, { kind: 'rounding' }> }) => {
  const { mode, lower, mid, upper, point, result, rangeMin, rangeMax, target, ask } = hint;
  const x0 = 70;
  const x1 = 530;
  const ly = 120;
  const span = upper - lower;
  const X = (v: number) => x0 + ((v - lower) / span) * (x1 - x0);

  const Axis = (
    <>
      <line x1={x0} y1={ly} x2={x1} y2={ly} stroke="#90A4AE" strokeWidth={3} strokeLinecap="round" />
      {/* end ticks + labels */}
      <line x1={x0} y1={ly - 7} x2={x0} y2={ly + 7} stroke="#90A4AE" strokeWidth={2} />
      <line x1={x1} y1={ly - 7} x2={x1} y2={ly + 7} stroke="#90A4AE" strokeWidth={2} />
      <text x={x0} y={ly + 22} textAnchor="middle" fontSize={12} fill="#777">{fmt(lower)}</text>
      <text x={x1} y={ly + 22} textAnchor="middle" fontSize={12} fill="#777">{fmt(upper)}</text>
    </>
  );

  if (mode === 'NORMAL' && point !== null && result !== null) {
    const up = result >= mid;
    const px = X(point);
    const rx = X(result);
    return (
      <g>
        {Axis}
        {/* midpoint threshold (5 の境目) */}
        <line x1={X(mid)} y1={ly - 18} x2={X(mid)} y2={ly + 10} stroke="#BDBDBD" strokeWidth={1.5} strokeDasharray="4 3" />
        <text x={X(mid)} y={ly - 24} textAnchor="middle" fontSize={11} fill="#9E9E9E">まん中 {fmt(mid)}</text>
        {/* the number */}
        <circle cx={px} cy={ly} r={5} fill="#F57C00" />
        <text x={px} y={ly - 12} textAnchor="middle" fontSize={13} fontWeight="bold" fill="#E65100">{fmt(point)}</text>
        {/* arrow toward the result end */}
        <line x1={px} y1={ly + 34} x2={rx} y2={ly + 34} stroke="#43A047" strokeWidth={2} />
        <polygon
          points={`${rx},${ly + 34} ${rx + (up ? -8 : 8)},${ly + 30} ${rx + (up ? -8 : 8)},${ly + 38}`}
          fill="#43A047"
        />
        <circle cx={rx} cy={ly} r={7} fill="none" stroke="#2E7D32" strokeWidth={2.5} />
        <text x={rx} y={ly + 52} textAnchor="middle" fontSize={13} fontWeight="bold" fill="#2E7D32">
          {up ? '切り上げ' : '切り捨て'} → {fmt(result)}
        </text>
      </g>
    );
  }

  // RANGE: shade the band [rangeMin, rangeMax] that maps to target
  if (mode === 'RANGE' && rangeMin !== null && rangeMax !== null && target !== null) {
    const bx0 = X(rangeMin);
    const bx1 = X(rangeMax);
    const askX = ask === 'MAX' ? bx1 : bx0;
    const askVal = ask === 'MAX' ? rangeMax : rangeMin;
    return (
      <g>
        <rect x={bx0} y={ly - 14} width={bx1 - bx0} height={28} fill="rgba(38,166,154,0.18)" stroke="#26A69A" strokeWidth={1.5} rx={4} />
        {Axis}
        <text x={(bx0 + bx1) / 2} y={ly - 24} textAnchor="middle" fontSize={13} fontWeight="bold" fill="#00695C">
          四捨五入すると {fmt(target)}
        </text>
        <line x1={bx0} y1={ly - 7} x2={bx0} y2={ly + 7} stroke="#00897B" strokeWidth={2} />
        <line x1={bx1} y1={ly - 7} x2={bx1} y2={ly + 7} stroke="#00897B" strokeWidth={2} />
        <text x={bx0} y={ly + 36} textAnchor="middle" fontSize={12} fontWeight="bold" fill="#00695C">{fmt(rangeMin)}</text>
        <text x={bx1} y={ly + 36} textAnchor="middle" fontSize={12} fontWeight="bold" fill="#00695C">{fmt(rangeMax)}</text>
        {/* "?" pointer on the asked end */}
        <text x={askX} y={ly - 40} textAnchor="middle" fontSize={18} fontWeight="bold" fill="#F57C00">?</text>
        <line x1={askX} y1={ly - 34} x2={askX} y2={ly - 16} stroke="#F57C00" strokeWidth={2} />
        <text x={askX} y={ly + 52} textAnchor="middle" fontSize={11} fill="#F57C00">
          いちばん{ask === 'MAX' ? '大きい' : '小さい'}（{fmt(askVal)}）
        </text>
      </g>
    );
  }

  return <g>{Axis}</g>;
};

// ---- unit conversion (単位の階段) -----------------------------------------

const UnitLadder = ({ hint }: { hint: Extract<Hint, { kind: 'unit' }> }) => {
  const { chain, fromUnit, toUnit } = hint;
  const n = chain.length;
  const x0 = 100;
  const x1 = 500;
  const by = 130;
  const bw = 64;
  const bh = 40;
  const xOf = (i: number) => (n === 1 ? 300 : x0 + (i * (x1 - x0)) / (n - 1));

  const fi = chain.findIndex((c) => c.unit === fromUnit);
  const ti = chain.findIndex((c) => c.unit === toUnit);
  const down = ti > fi;
  let factor = 1;
  for (let k = Math.min(fi, ti); k < Math.max(fi, ti); k++) factor *= chain[k].factor!;
  const fx = xOf(fi);
  const tx = xOf(ti);
  const midArrow = (fx + tx) / 2;

  return (
    <g>
      {/* connectors with the ×factor between adjacent units */}
      {chain.slice(0, -1).map((c, i) => {
        const xa = xOf(i) + bw / 2;
        const xb = xOf(i + 1) - bw / 2;
        return (
          <g key={`c${i}`}>
            <line x1={xa} y1={by} x2={xb} y2={by} stroke="#B0BEC5" strokeWidth={2} />
            <text x={(xa + xb) / 2} y={by - 6} textAnchor="middle" fontSize={12} fill="#78909C">
              ×{c.factor}
            </text>
          </g>
        );
      })}
      {/* unit boxes (highlight from / to) */}
      {chain.map((c, i) => {
        const hi = c.unit === fromUnit || c.unit === toUnit;
        return (
          <g key={c.unit}>
            <rect
              x={xOf(i) - bw / 2} y={by - bh / 2} width={bw} height={bh} rx={8}
              fill={hi ? '#5C6BC0' : '#ECEFF1'} stroke={hi ? '#3949AB' : '#CFD8DC'} strokeWidth={2}
            />
            <text
              x={xOf(i)} y={by} textAnchor="middle" dominantBaseline="central"
              fontSize={18} fontWeight="bold" fill={hi ? '#fff' : '#90A4AE'}
            >
              {c.unit}
            </text>
          </g>
        );
      })}
      {/* direction arrow + total factor */}
      <path d={`M ${fx} ${by - 28} Q ${midArrow} ${by - 62} ${tx} ${by - 28}`} fill="none" stroke="#F57C00" strokeWidth={2.5} />
      <polygon points={`${tx},${by - 20} ${tx - 5},${by - 30} ${tx + 5},${by - 30}`} fill="#F57C00" />
      <text x={midArrow} y={by - 66} textAnchor="middle" fontSize={15} fontWeight="bold" fill="#E65100">
        {down ? '×' : '÷'}{factor}
      </text>
    </g>
  );
};

// ---- decimal column 筆算 --------------------------------------------------

const DECX = 340; // x of the decimal point (numbers align here)

const DecNumber = ({ y, value, color, bold }: { y: number; value: number; color: string; bold?: boolean }) => {
  const [ip, fp] = fmt(value).split('.');
  return (
    <g fill={color} fontFamily="monospace" fontSize={22} fontWeight={bold ? 'bold' : 'normal'}>
      <text x={DECX - 3} y={y} textAnchor="end">{ip}</text>
      {fp !== undefined && (
        <>
          <text x={DECX} y={y} textAnchor="middle">.</text>
          <text x={DECX + 5} y={y} textAnchor="start">{fp}</text>
        </>
      )}
    </g>
  );
};

const DecCalc = ({ hint }: { hint: Extract<Hint, { kind: 'deccalc' }> }) => {
  const { rows, result } = hint;
  const startY = 44;
  const rowH = 30;
  const xOp = 130;
  const lineY = startY + (rows.length - 1) * rowH + 14;
  return (
    <g>
      {rows.map((r, i) => {
        const y = startY + i * rowH;
        return (
          <g key={i}>
            {r.op && (
              <text x={xOp} y={y} fontFamily="monospace" fontSize={22} fill="#374151" textAnchor="middle">
                {r.op}
              </text>
            )}
            <DecNumber y={y} value={r.value} color="#374151" />
          </g>
        );
      })}
      <line x1={xOp - 10} y1={lineY} x2={DECX + 70} y2={lineY} stroke="#374151" strokeWidth={2} />
      <DecNumber y={lineY + 26} value={result} color="#2E7D32" bold />
    </g>
  );
};

// ---- decimal place composition --------------------------------------------

const DecCompose = ({ hint }: { hint: Extract<Hint, { kind: 'deccompose' }> }) => {
  const { cells, number } = hint;
  const boxW = 92;
  const gap = 46;
  const h = 58;
  const cy = 86;
  const n = cells.length;
  const totalW = n * boxW + (n - 1) * gap + 150;
  const startX = Math.max(10, (600 - totalW) / 2);
  const eqX = startX + n * boxW + (n - 1) * gap;
  return (
    <g fontFamily="sans-serif">
      {cells.map((c, i) => {
        const x = startX + i * (boxW + gap);
        const cxv = x + boxW / 2;
        return (
          <g key={i}>
            <rect x={x} y={cy - h / 2} width={boxW} height={h} rx={10} fill="#E0F7FA" stroke="#00ACC1" strokeWidth={2} />
            <text x={cxv} y={cy - 4} textAnchor="middle" fontSize={20} fontWeight="bold" fill="#00838F">{c.place}</text>
            <text x={cxv} y={cy + 18} textAnchor="middle" fontSize={14} fill="#00838F">が{c.count}こ</text>
            {i < n - 1 && (
              <text x={x + boxW + gap / 2} y={cy + 6} textAnchor="middle" fontSize={22} fontWeight="bold" fill="#607D8B">＋</text>
            )}
          </g>
        );
      })}
      <text x={eqX + 26} y={cy + 6} textAnchor="middle" fontSize={22} fontWeight="bold" fill="#607D8B">＝</text>
      <text x={eqX + 52} y={cy + 9} textAnchor="start" fontSize={30} fontWeight="bold" fill="#006064">{fmt(number)}</text>
    </g>
  );
};

const Bunpai = ({ hint }: { hint: Extract<Hint, { kind: 'bunpai' }> }) => {
  const { total, baseMultiplier, offset, target, baseLabel, multipliedLabel } = hint;
  const base = (total - offset) / (1 + baseMultiplier);
  const mult = base * baseMultiplier + offset;
  const maxVal = Math.max(base, mult);
  const topW = (base / maxVal) * BAR_MAXW; // base bar = 1 unit
  const botW = (mult / maxVal) * BAR_MAXW;
  const unit = topW;
  return (
    <g>
      <Bar y={BAR_Y1} w={topW} />
      <Bar y={BAR_Y2} w={botW} />
      <text x={BAR_X0 - 10} y={BAR_Y1 + 4} textAnchor="end" fontSize={13} fontWeight="bold" fill="#333">{baseLabel}</text>
      <text x={BAR_X0 - 10} y={BAR_Y2 + 4} textAnchor="end" fontSize={13} fontWeight="bold" fill="#333">{multipliedLabel}</text>
      <SumBracket w={Math.max(topW, botW)} label={`合計 ${total}`} />
      {Array.from({ length: baseMultiplier - 1 }, (_, i) => {
        const sx = BAR_X0 + unit * (i + 1);
        return <line key={i} x1={sx} y1={BAR_Y2 - TICK} x2={sx} y2={BAR_Y2 + TICK} stroke="#999" strokeWidth={1} strokeDasharray="3 3" />;
      })}
      {offset !== 0 && (() => {
        const startX = BAR_X0 + unit * baseMultiplier;
        const endX = BAR_X0 + botW;
        const color = offset > 0 ? '#E91E63' : '#2196F3';
        return (
          <g>
            <line x1={Math.min(startX, endX)} y1={BAR_Y2 + 9} x2={Math.max(startX, endX)} y2={BAR_Y2 + 9} stroke={color} strokeWidth={2} />
            <text x={(startX + endX) / 2} y={BAR_Y2 + 22} textAnchor="middle" fontSize={11} fontWeight="bold" fill={color}>
              {offset > 0 ? '+' : '−'}{Math.abs(offset)}
            </text>
          </g>
        );
      })()}
      <Unknown y={target === 'BASE' ? BAR_Y1 : BAR_Y2} w={target === 'BASE' ? topW : botW} />
    </g>
  );
};
